import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Autorenew as SubscriptionsIcon,
  CheckCircle as ActiveIcon,
  Warning as PastDueIcon,
  Block as CanceledIcon,
} from '@mui/icons-material';
import { adminApi } from '../../api';
import { useDocumentTitle, getStoredRowsPerPage, setStoredRowsPerPage } from '../../hooks/core';

interface AdminSubscription {
  id: string;
  userId: string;
  productId: string;
  priceId: string;
  stripeSubscriptionId: string;
  status: 'active' | 'past_due' | 'canceled' | 'unpaid';
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  mode: 'test' | 'live';
  createdAt: string;
  user?: {
    id: string;
    email: string;
    displayName: string;
  };
  product?: {
    id: string;
    name: string;
  };
}

export function AdminSubscriptionsPage() {
  useDocumentTitle('Subscriptions Management');

  const [subscriptions, setSubscriptions] = useState<AdminSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => getStoredRowsPerPage());
  const [statusFilter, setStatusFilter] = useState<string>('');

  const fetchSubscriptions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminApi.listSubscriptions({
        page: page + 1,
        pageSize: rowsPerPage,
        status: statusFilter || undefined,
      });
      setSubscriptions(response.subscriptions || []);
      setTotal(response.total || 0);
      setError(null);
    } catch (err) {
      setError('Failed to load subscriptions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter]);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusChip = (status: string, cancelAtPeriodEnd: boolean) => {
    if (cancelAtPeriodEnd && status === 'active') {
      return { label: 'Canceling', color: 'warning' as const, icon: <PastDueIcon /> };
    }
    switch (status) {
      case 'active':
        return { label: 'Active', color: 'success' as const, icon: <ActiveIcon /> };
      case 'past_due':
        return { label: 'Past Due', color: 'error' as const, icon: <PastDueIcon /> };
      case 'canceled':
        return { label: 'Canceled', color: 'default' as const, icon: <CanceledIcon /> };
      case 'unpaid':
        return { label: 'Unpaid', color: 'error' as const, icon: <PastDueIcon /> };
      default:
        return { label: status, color: 'default' as const, icon: null };
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <SubscriptionsIcon color="primary" sx={{ fontSize: 32 }} />
        <Typography variant="h4">Subscriptions</Typography>
      </Box>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        View and manage all user subscriptions.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="past_due">Past Due</MenuItem>
            <MenuItem value="canceled">Canceled</MenuItem>
            <MenuItem value="unpaid">Unpaid</MenuItem>
          </Select>
        </FormControl>
      </Paper>

      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Plan</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Period</TableCell>
                    <TableCell>Mode</TableCell>
                    <TableCell>Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {subscriptions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          No subscriptions found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    subscriptions.map((subscription) => {
                      const statusChip = getStatusChip(subscription.status, subscription.cancelAtPeriodEnd);
                      return (
                        <TableRow key={subscription.id} hover>
                          <TableCell>
                            <Typography fontWeight="medium">
                              {subscription.user?.displayName || 'Unknown'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {subscription.user?.email}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {subscription.product?.name || 'Unknown Plan'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={statusChip.label}
                              color={statusChip.color}
                              size="small"
                              icon={statusChip.icon || undefined}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDate(subscription.currentPeriodStart)} - {formatDate(subscription.currentPeriodEnd)}
                            </Typography>
                            {subscription.cancelAtPeriodEnd && (
                              <Typography variant="caption" color="warning.main">
                                Cancels at period end
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={subscription.mode.toUpperCase()}
                              size="small"
                              color={subscription.mode === 'live' ? 'success' : 'default'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            {formatDate(subscription.createdAt)}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                const newSize = parseInt(e.target.value, 10);
                setRowsPerPage(newSize);
                setStoredRowsPerPage(newSize);
                setPage(0);
              }}
              rowsPerPageOptions={[10, 20, 50]}
            />
          </>
        )}
      </Paper>
    </Box>
  );
}
